import { useEffect } from 'react';
import { Stack, Group, Card, Title, Text, Button, TextInput, Textarea, Select, ActionIcon, Center, Accordion } from '@mantine/core';
import { IconArrowLeft, IconSpeakerphone, IconTrash, IconDeviceFloppy, IconPencil, IconUser } from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useClasses } from '../hooks/useLookups';
import { SectionCard } from '../components/SectionCard';
import { BlockLoader } from '../components/BlockLoader';
import { confirmDelete } from '../lib/confirm';

interface AnnouncementDetailData {
  id: string; title: string; body: string; audience: string; classId?: string | null;
  linkScreen?: string | null; linkUrl?: string | null;
  titleRu?: string | null; bodyRu?: string | null; titleEn?: string | null; bodyEn?: string | null;
  createdBy?: { id: string; fullName: string } | null;
  class?: { id: string; name: string } | null;
}

const apiErr = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

/** E'lon detali — ma'lumot + tahrirlash + o'chirish (bir sahifada). */
export default function AnnouncementDetail() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: classes } = useClasses();

  const AUDIENCE_LABELS: Record<string, string> = {
    ALL: t('bannersPage.audAll'), STAFF: t('nav.staff'), TEACHERS: t('bannersPage.audTeachers'),
    PARENTS: t('bannersPage.audParents'), STUDENTS: t('nav.students'), CLASS: t('fields.class'),
  };
  const APP_SCREENS: Record<string, string> = {
    Attendance: t('nav.attendance'), Grades: t('nav.grades'), Homework: t('nav.homework'), Payments: t('studentDetailPage.tabPayments'), Announcements: t('nav.announcements'),
  };

  const { data: announcement, isLoading } = useQuery({
    queryKey: ['announcement', id],
    queryFn: async () => (await api.get<AnnouncementDetailData>(`/announcements/${id}`)).data,
    enabled: !!id,
  });

  const form = useForm({
    initialValues: { title: '', body: '', titleRu: '', bodyRu: '', titleEn: '', bodyEn: '', audience: 'ALL', classId: '', linkType: 'none', linkScreen: '', linkUrl: '' },
    validate: {
      title: (v) => (!v ? t('homeworkPage.titleRequired') : null),
      body: (v) => (!v ? t('announcementsPage.bodyRequired') : null),
      classId: (v, values) => (values.audience === 'CLASS' && !v ? t('homeworkPage.selectClass') : null),
      linkScreen: (v, values) => (values.linkType === 'app' && !v ? t('announcementsPage.selectScreen') : null),
      linkUrl: (v, values) => (values.linkType === 'url' && !/^https?:\/\//.test(v) ? t('announcementsPage.fullUrlRequired') : null),
    },
  });

  useEffect(() => {
    if (announcement) form.setValues({
      title: announcement.title,
      body: announcement.body,
      titleRu: announcement.titleRu ?? '',
      bodyRu: announcement.bodyRu ?? '',
      titleEn: announcement.titleEn ?? '',
      bodyEn: announcement.bodyEn ?? '',
      audience: announcement.audience,
      classId: announcement.classId ?? '',
      linkType: announcement.linkUrl ? 'url' : announcement.linkScreen ? 'app' : 'none',
      linkScreen: announcement.linkScreen ?? '',
      linkUrl: announcement.linkUrl ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcement?.id]);

  const isClassAudience = form.values.audience === 'CLASS';

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['announcement', id] });
    qc.invalidateQueries({ queryKey: ['announcements'] });
  };

  const saveMut = useMutation({
    mutationFn: (v: typeof form.values) =>
      api.patch(`/announcements/${id}`, {
        title: v.title,
        body: v.body,
        titleRu: v.titleRu || null,
        bodyRu: v.bodyRu || null,
        titleEn: v.titleEn || null,
        bodyEn: v.bodyEn || null,
        audience: v.audience,
        classId: v.audience === 'CLASS' ? v.classId : null,
        linkScreen: v.linkType === 'app' ? v.linkScreen : null,
        linkUrl: v.linkType === 'url' ? v.linkUrl : null,
      }),
    onSuccess: () => { notifications.show({ color: 'green', message: t('common.save') }); invalidate(); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/announcements/${id}`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('announcementDetail.deleted') }); invalidate(); navigate('/announcements'); },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });

  if (isLoading) return <BlockLoader variant="card" />;
  if (!announcement) return <Center h={300}><Text c="dimmed">{t('announcementDetail.notFound')}</Text></Center>;

  return (
    <Stack maw={640}>
      <Group>
        <ActionIcon variant="default" size="lg" onClick={() => navigate('/announcements')}><IconArrowLeft size={18} /></ActionIcon>
        <Group gap="xs"><IconSpeakerphone size={22} color="var(--mantine-color-brand-6)" /><Title order={4}>{announcement.title}</Title></Group>
      </Group>

      {/* Kim yaratdi (read-only) */}
      <SectionCard title={t('announcementDetail.createdBy')} icon={IconUser}>
        <Text fw={500}>{announcement.createdBy?.fullName ?? '—'}</Text>
      </SectionCard>

      {/* Tahrirlash */}
      <SectionCard title={t('announcementDetail.edit')} icon={IconPencil}>
        <form onSubmit={form.onSubmit((v) => saveMut.mutate(v))}>
          <Stack>
            <TextInput label={t('announcementsPage.titleUz')} required {...form.getInputProps('title')} />
            <Textarea label={t('announcementsPage.bodyUz')} minRows={3} autosize required {...form.getInputProps('body')} />
            <Accordion variant="contained">
              <Accordion.Item value="translations">
                <Accordion.Control>{t('announcementsPage.translations')}</Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    <TextInput label={t('announcementsPage.titleRu')} {...form.getInputProps('titleRu')} />
                    <Textarea label={t('announcementsPage.bodyRu')} minRows={2} autosize {...form.getInputProps('bodyRu')} />
                    <TextInput label={t('announcementsPage.titleEn')} {...form.getInputProps('titleEn')} />
                    <Textarea label={t('announcementsPage.bodyEn')} minRows={2} autosize {...form.getInputProps('bodyEn')} />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
            <Select
              label={t('announcementsPage.audienceTo')}
              data={Object.entries(AUDIENCE_LABELS).map(([value, label]) => ({ value, label }))}
              allowDeselect={false}
              {...form.getInputProps('audience')}
              onChange={(v) => {
                form.setFieldValue('audience', v ?? 'ALL');
                if (v !== 'CLASS') form.setFieldValue('classId', '');
              }}
            />
            {isClassAudience && (
              <Select
                label={t('announcementsPage.whichClass')}
                placeholder={t('homeworkPage.selectClass')}
                data={(classes ?? []).map((c) => ({ value: c.id, label: c.name }))}
                searchable
                required
                {...form.getInputProps('classId')}
              />
            )}
            <Select
              label={t('announcementsPage.linkOptional')}
              data={[
                { value: 'none', label: t('announcementsPage.linkNone') },
                { value: 'app', label: t('announcementsPage.linkApp') },
                { value: 'url', label: t('announcementsPage.linkUrl') },
              ]}
              allowDeselect={false}
              {...form.getInputProps('linkType')}
            />
            {form.values.linkType === 'app' && (
              <Select
                label={t('announcementsPage.appScreenLabel')}
                placeholder={t('announcementsPage.selectScreenPlaceholder')}
                data={Object.entries(APP_SCREENS).map(([value, label]) => ({ value, label }))}
                required
                {...form.getInputProps('linkScreen')}
              />
            )}
            {form.values.linkType === 'url' && (
              <TextInput label={t('announcementsPage.url')} placeholder="https://..." required {...form.getInputProps('linkUrl')} />
            )}
            <Button type="submit" leftSection={<IconDeviceFloppy size={16} />} loading={saveMut.isPending}>{t('common.save')}</Button>
          </Stack>
        </form>
      </SectionCard>

      {/* O'chirish */}
      <Card withBorder style={{ borderColor: 'var(--mantine-color-red-3)' }}>
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" c="dimmed">{t('announcementDetail.deleteHint')}</Text>
          <Button color="red" variant="light" leftSection={<IconTrash size={16} />} onClick={() => confirmDelete(announcement.title, () => deleteMut.mutate())} loading={deleteMut.isPending}>
            {t('common.delete')}
          </Button>
        </Group>
      </Card>
    </Stack>
  );
}
